import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { HeroBackground } from "@/components/HeroBackground";
import { SectionDecor } from "@/components/SectionDecor";
import { CredentialsSection } from "@/components/CredentialsSection";
import aboutHero from "@/assets/mckay/team-1981.webp.asset.json";


export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About McKay Roofing & Solar – Southern California Since 1981" },
      { name: "description", content: "Family-owned roofing and solar company serving Southern California since 1981. Meet the McKay crew and the standards behind our work." },
      { name: "keywords", content: "about McKay Roofing & Solar, Southern California roofing company, Southern California solar installer, family owned roofing California, Carlsbad roofing contractor" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "publisher", content: "McKay Roofing & Solar" },
      { property: "og:title", content: "About McKay Roofing & Solar" },
      { property: "og:description", content: "Family-run roofing and solar crew founded in Southern California, serving all of Southern California since 1981." },
      { property: "og:url", content: "https://mckay.steadyautogrowthdemo.com/about" },
    ],
    links: [
      { rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/about" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "McKay Roofing & Solar",
          url: "https://mckay.steadyautogrowthdemo.com",
          logo: "https://mckay.steadyautogrowthdemo.com/favicon.png",
          telephone: "+1 858-541-1097",
          email: "sales@mckayroofingsolar.com",
          address: {
            "@type": "PostalAddress",
            addressLocality: "San Diego",
            addressRegion: "CA",
            addressCountry: "US",
          },
          areaServed: "Southern California, CA",
          foundingDate: "1981",
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground src={aboutHero.url} alt="McKay Roofing & Solar crew, Southern California, since 1981" />
          <div className="relative mx-auto flex min-h-[85vh] max-w-4xl flex-col justify-center px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-28">
            <h1 className="text-5xl font-extrabold sm:text-6xl text-balance">A Southern California crew since 1981.</h1>
            <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">McKay Roofing &amp; Solar started as a small family roofing business in Carlsbad in 1981. Four decades later we're still family-owned – now handling roofing and solar across all of Southern California.</p>
          </div>
        </section>

        <section className="relative overflow-hidden">
          <SectionDecor variant="house" side="right" tone="primary" opacity={0.05} />
          <div className="relative z-10 mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none space-y-5 text-base leading-relaxed">
            <p>The short version: we grew up here. We know what Southern California sun does to a roof, what a coastal breeze does to shingles, and what a hillside install actually takes. We started this company because we kept seeing homeowners get sold roofs and solar systems that didn't fit their house.</p>

            <p>So we built the company we'd want working on our own homes. Roofing done right the first time. Solar sized and installed to actually pay off. A crew that shows up on time, in uniform, and treats every property like it's their own.</p>

            <h2 className="text-2xl font-extrabold text-primary mt-10">Where we work</h2>
            <p>We're based in Carlsbad. From there, our trucks reach every city in Southern California – from Pine Valley and Winter Gardens in the east, down through Coronado and Oceanside, and out to Vista, Ramona and Fallbrook. Coastal roofs, inland roofs, hillside solar arrays – we've worked on all of it.</p>

            <h2 className="text-2xl font-extrabold text-primary mt-10">How we hire</h2>
            <p>Every person on our crew is full-time and on payroll. No day labor, no rotating subcontractors. Our roofers are trained on shingle, tile, metal and flat systems. Our solar installers are certified on the panels, inverters and batteries we install – and every one of them understands the roof underneath.</p>

            <h2 className="text-2xl font-extrabold text-primary mt-10">What we believe</h2>
            <p>That a roof is the single most important system on your house. That solar only pays off if it's designed for your roof and your usage. That nobody should have to call you back twice to fix the same leak. And that the best advertising we can do is a good job for a neighbor who'll tell another neighbor.</p>

            <h2 className="text-2xl font-extrabold text-primary mt-10">Where to start</h2>
            <p>See everything we do on our <Link to="/services/roofing" className="text-accent font-semibold hover:underline">roofing</Link> and <Link to="/services/solar" className="text-accent font-semibold hover:underline">solar</Link> pages, or just <Link to="/contact" className="text-accent font-semibold hover:underline">send us a message</Link> and we'll figure out the right fit.</p>
          </div>
          </div>
        </section>

        <CredentialsSection showFeaturedCerts />




        <CTABlock />
      </main>
      <SiteFooter />
    </div>
  );
}
